import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";

const isServerSide = () => typeof window === "undefined";

export function createUseLocalStorage({ getStorageKey, postStorageMessage }) {
  return function useLocalStorage(key, defaultValue = null) {
    const defaultValueRef = useRef(defaultValue);
    defaultValueRef.current = defaultValue;

    const canonicalKey = useMemo(() => getStorageKey(key), [key]);
    const [isHydrated, setIsHydrated] = useState(false);
    const hydrationRef = useRef(0);

    const { data: value = defaultValueRef.current, mutate } = useSWR(
      canonicalKey,
      null,
      {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        refreshWhenHidden: false,
        refreshWhenOffline: false,
      },
    );

    const readStoredValue = useCallback(
      (fallback) => {
        if (isServerSide()) return fallback;

        const storedValue = window.localStorage.getItem(canonicalKey);
        if (storedValue === null || storedValue === "undefined") return fallback;

        try {
          return JSON.parse(storedValue);
        } catch {
          return fallback;
        }
      },
      [canonicalKey],
    );

    useEffect(() => {
      const hydration = hydrationRef.current + 1;
      hydrationRef.current = hydration;
      let active = true;
      setIsHydrated(false);

      if (isServerSide()) return () => {};

      void mutate(readStoredValue(defaultValueRef.current), {
        revalidate: false,
      }).then(() => {
        if (active && hydrationRef.current === hydration) {
          setIsHydrated(true);
        }
      });

      return () => {
        active = false;
      };
    }, [canonicalKey, mutate, readStoredValue]);

    const setValue = useCallback(
      async (valueOrUpdater) => {
        const resolveValue = (current) =>
          typeof valueOrUpdater === "function"
            ? valueOrUpdater(current)
            : valueOrUpdater;
        let resolvedValue = null;

        await mutate(
          (current) => {
            const fromStorage = readStoredValue(defaultValueRef.current);
            resolvedValue = resolveValue(
              current ?? fromStorage ?? defaultValueRef.current ?? null,
            );
            return resolvedValue;
          },
          { revalidate: false },
        );

        if (isServerSide()) return;
        const localStorageValue = JSON.stringify(resolvedValue);
        window.localStorage.setItem(canonicalKey, localStorageValue);
        postStorageMessage?.({
          key: canonicalKey,
          value: localStorageValue,
          action: "set",
        });
      },
      [canonicalKey, mutate, readStoredValue],
    );

    const removeValue = useCallback(async () => {
      await mutate(defaultValueRef.current, { revalidate: false });
      if (isServerSide()) return;

      window.localStorage.removeItem(canonicalKey);
      postStorageMessage?.({
        key: canonicalKey,
        value: "null",
        action: "remove",
      });
    }, [canonicalKey, mutate]);

    return [value, setValue, removeValue, isHydrated];
  };
}
